import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Check, LayoutGrid, Sparkles } from 'lucide-react';
import ToolMonogram from './ToolMonogram';
import { useSidebarTools } from './sidebarTools';

/*
  SidebarToolsMenu — the "Add tools" menu of the Tools rail (2026-10-02): every tool the person
  could keep in the sidebar — their own, then the ones approved for the team — as checkboxes, plus
  the way to build a new one and to the shelf where tools are edited or deleted. Opens beside the
  rail like the folder flyouts (portal, fixed, kept on screen), closes on Esc or a click elsewhere.
*/
const FONT = '"Helvetica Neue", Helvetica, Arial, sans-serif';
const WIDTH = 288;

export default function SidebarToolsMenu({ anchor, userId, onClose, onNavigate }) {
  const rail = useSidebarTools(userId);
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  // Beside the button, inside the viewport (the rail can sit in the phone drawer).
  useLayoutEffect(() => {
    const place = () => {
      const r = anchor?.getBoundingClientRect?.();
      if (!r) return;
      const h = ref.current?.offsetHeight || 320;
      const w = Math.min(WIDTH, window.innerWidth - 16);
      let left = r.right + 12;
      if (left + w > window.innerWidth - 8) left = Math.max(8, window.innerWidth - w - 8);
      const top = Math.max(8, Math.min(r.top - 8, window.innerHeight - h - 8));
      setPos({ left, top, width: w, caret: Math.min(Math.max(r.top + r.height / 2 - top, 14), h - 14) });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [anchor, rail.available.length, rail.tools.length, error]);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current?.contains(e.target) || anchor?.contains?.(e.target)) return;
      onClose();
    };
    // Esc closes this menu only — not the phone drawer the rail may sit in (it listens on window).
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey, true); };
  }, [anchor, onClose]);
  useEffect(() => { ref.current?.querySelector('[role="menuitemcheckbox"]')?.focus({ preventScroll: true }); }, []);

  const flip = async (tool, on) => {
    setBusyId(tool.id); setError(null);
    try {
      if (on) await rail.remove(tool.id);
      else await rail.add(tool);
    } catch (e) { setError(e.message); }
    finally { setBusyId(null); }
  };

  // Everything that could be listed: what is available now, plus rail entries that are not
  // (an admin's pick of somebody else's tool) so they can still be taken out.
  const all = [...rail.available, ...rail.tools.filter((t) => !rail.available.some((a) => a.id === t.id))];
  const mine = all.filter((t) => t.mine);
  const others = all.filter((t) => !t.mine);
  const full = rail.tools.length >= rail.max;

  const row = (t) => {
    const on = rail.has(t.id);
    const blocked = !on && full;
    return (
      <button
        key={t.id}
        type="button"
        role="menuitemcheckbox"
        aria-checked={on}
        disabled={busyId === t.id || blocked}
        onClick={() => flip(t, on)}
        title={blocked ? `The sidebar holds up to ${rail.max} tools — take one out first` : (t.description || t.name)}
        className="group w-full flex items-center gap-2.5 h-9 px-2.5 rounded-lg text-left outline-none transition-colors hover:bg-[#f5f5f5] dark:hover:bg-white/[0.05] focus-visible:bg-[#f5f5f5] dark:focus-visible:bg-white/[0.05] disabled:opacity-45 disabled:hover:bg-transparent"
      >
        <ToolMonogram name={t.name} size={13} className={`shrink-0 ${on ? 'text-[#111111] dark:text-white' : 'text-[#9e9ea0] dark:text-white/40'}`} />
        <span className={`flex-1 min-w-0 truncate text-[13px] tracking-tight ${on ? 'font-medium text-[#111111] dark:text-white' : 'text-[#707072] dark:text-white/60'}`}>{t.name}</span>
        {!t.mine && t.owner?.name && <span className="shrink-0 max-w-[76px] truncate text-[10px] text-[#9e9ea0] dark:text-white/35">{t.owner.name.split(' ')[0]}</span>}
        <span aria-hidden className={`shrink-0 size-4 rounded-[4px] border flex items-center justify-center transition-colors ${on
          ? 'bg-[#111111] border-[#111111] text-white dark:bg-white dark:border-white dark:text-[#111111]'
          : 'border-[#cacacb] dark:border-white/25 group-hover:border-[#707072] dark:group-hover:border-white/50'}`}>
          {on && <Check size={11} strokeWidth={3} />}
        </span>
      </button>
    );
  };
  const heading = (text, extra = null) => (
    <div className="flex items-center justify-between px-2.5 pt-2 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#9e9ea0] dark:text-white/40">
      <span>{text}</span>{extra}
    </div>
  );

  return createPortal(
    <motion.div
      ref={ref}
      role="menu"
      aria-label="Tools in the sidebar"
      initial={{ opacity: 0, x: -6, scale: 0.98 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      transition={{ duration: 0.16, ease: [0.4, 0, 0.2, 1] }}
      className="fixed z-[100] p-1.5 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#141414] shadow-xl"
      style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, width: pos?.width ?? WIDTH, transformOrigin: 'left top', fontFamily: FONT }}
    >
      {pos && (
        <span aria-hidden="true" className="absolute -left-1 w-2 h-2 -translate-y-1/2 rotate-45 bg-white dark:bg-[#141414] border-l border-b border-gray-200 dark:border-white/10" style={{ top: pos.caret }} />
      )}
      <div className="max-h-[min(420px,70vh)] overflow-y-auto overflow-x-hidden [scrollbar-width:thin]">
        {heading('Your tools', <span className="tabular-nums tracking-[0.08em]">{rail.tools.length} / {rail.max} in sidebar</span>)}
        {mine.length ? mine.map(row) : (
          <p className="px-2.5 pb-2 text-[12px] leading-snug text-[#9e9ea0] dark:text-white/40">You have not built a tool yet.</p>
        )}
        {others.length > 0 && (
          <>
            {heading('Team tools')}
            {others.map(row)}
          </>
        )}
      </div>
      {error && <p role="alert" className="px-2.5 py-1.5 text-[11.5px] leading-snug text-[#E24B4A]">{error}</p>}
      <div className="mt-1 pt-1 border-t border-gray-200 dark:border-white/10">
        <button type="button" role="menuitem" onClick={() => onNavigate('/tools/builder')}
          className="w-full flex items-center gap-2.5 h-9 px-2.5 rounded-lg text-left text-[13px] font-medium tracking-tight text-[#0E8578] dark:text-[#35C4B5] hover:bg-[#0E8578]/[0.06] transition-colors">
          <Sparkles size={14} strokeWidth={2.25} className="shrink-0" />Build a tool
        </button>
        <button type="button" role="menuitem" onClick={() => onNavigate('/tools#my-tools')}
          className="w-full flex items-center gap-2.5 h-9 px-2.5 rounded-lg text-left text-[13px] tracking-tight text-[#707072] dark:text-white/60 hover:bg-[#f5f5f5] dark:hover:bg-white/[0.05] hover:text-[#111111] dark:hover:text-white transition-colors">
          <LayoutGrid size={14} strokeWidth={2.25} className="shrink-0" />All my tools
        </button>
      </div>
    </motion.div>,
    document.body
  );
}
